import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import { useAuth } from '../../hooks/useAuth';
import { adminDialogPaperSx, adminShellBackgroundSx } from '../../components/admin/adminVisuals';
import { adminPath } from '../../config/adminPath';

type LocationState = {
  from?: string;
  passwordUpdated?: boolean;
};

export default function AdminLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isConfigured, isLoading, user, signInWithPassword, requestPasswordReset } = useAuth();

  const state = (location.state as LocationState | null) ?? null;
  const redirectTo = useMemo(() => state?.from || adminPath, [state?.from]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [localSetupUrl, setLocalSetupUrl] = useState<string | null>(null);

  useEffect(() => {
    if (state?.passwordUpdated) {
      setFeedback({
        type: 'success',
        message: 'Senha definida com sucesso. Agora faça seu login para acessar o painel.',
      });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, navigate, state?.passwordUpdated]);

  if (!isLoading && user) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);
    setIsSubmitting(true);

    const error = await signInWithPassword({ email, password });

    setIsSubmitting(false);

    if (error) {
      setFeedback({ type: 'error', message: error });
      return;
    }

    setFeedback({ type: 'success', message: 'Login realizado com sucesso.' });
  };

  const handleReset = async () => {
    setFeedback(null);
    setIsSubmitting(true);

    const result = await requestPasswordReset(resetEmail || email);

    setIsSubmitting(false);

    if (result.error) {
      setFeedback({ type: 'error', message: result.error });
      return;
    }

    setResetOpen(false);
    setLocalSetupUrl(result.setupUrl || null);
    setFeedback({
      type: 'info',
      message: result.setupUrl
        ? 'O SMTP local não está configurado. Use o link de definição de senha exibido abaixo.'
        : 'Se o e-mail existir no projeto, um link para definir nova senha foi enviado para a caixa de entrada.',
    });
  };

  return (
    <Box
      sx={{
        ...adminShellBackgroundSx,
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 6,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 1120,
          overflow: 'hidden',
          borderRadius: 5,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: '0 28px 60px rgba(15, 23, 42, 0.08)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(16px)',
        }}
      >
        <Box
          sx={{
            p: { xs: 4, md: 6 },
            position: 'relative',
            overflow: 'hidden',
            background:
              'radial-gradient(circle at top right, rgba(14, 165, 233, 0.18), transparent 34%), linear-gradient(160deg, #f8fbff 0%, #edf4ff 58%, #eefaf7 100%)',
            '&::after': {
              content: '""',
              position: 'absolute',
              right: -70,
              bottom: -90,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background: 'rgba(37, 99, 235, 0.08)',
            },
          }}
        >
          <Stack spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1.8, color: '#3b5b7e', fontWeight: 700 }}>
              Faculdade Senac
            </Typography>
            <Typography variant="h3" fontWeight={900} sx={{ color: '#16324f' }}>
              Painel Administrativo
            </Typography>
            <Typography sx={{ color: 'rgba(22, 50, 79, 0.86)', maxWidth: 520 }}>
              Um ambiente reservado para organizar conteúdos institucionais e controlar acessos com mais clareza no dia
              a dia.
            </Typography>

            <Stack spacing={1.25} sx={{ pt: 2 }}>
              <Typography fontWeight={800} sx={{ color: '#16324f' }}>
                Você encontra aqui:
              </Typography>
              <Typography color="text.secondary">Entrada por e-mail e senha.</Typography>
              <Typography color="text.secondary">Rotas administrativas protegidas.</Typography>
              <Typography color="text.secondary">Fluxo de redefinição de senha por e-mail.</Typography>
              <Typography color="text.secondary">Espaço organizado para atualização de conteúdos.</Typography>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 3, md: 5 }, bgcolor: 'rgba(255,255,255,0.9)' }}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="h5" fontWeight={900} sx={{ color: '#16324f' }}>
                Entrar
              </Typography>
              <Typography color="text.secondary">
                Use suas credenciais administrativas para acessar o painel.
              </Typography>
            </Stack>

            {!isConfigured && (
              <Alert severity="warning">O acesso ao painel ainda não está disponível neste ambiente.</Alert>
            )}

            {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
            {localSetupUrl && (
              <Button
                component="a"
                href={localSetupUrl}
                variant="outlined"
                sx={{ alignSelf: 'flex-start', borderRadius: 999 }}
              >
                Abrir link local de senha
              </Button>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="E-mail"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setResetEmail(event.target.value);
                  }}
                  InputProps={{
                    startAdornment: <MailOutlineRoundedIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  fullWidth
                  required
                />

                <TextField
                  label="Senha"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  InputProps={{
                    startAdornment: <LockRoundedIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  fullWidth
                  required
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting || !isConfigured}
                  sx={{ borderRadius: 999 }}
                >
                  {isSubmitting ? 'Entrando...' : 'Acessar painel'}
                </Button>

                <Button
                  variant="text"
                  onClick={() => setResetOpen(true)}
                  disabled={isSubmitting || !isConfigured}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Definir nova senha por e-mail
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Dialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: adminDialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#16324f' }}>Enviar link de senha</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography color="text.secondary">
              Informe o e-mail administrativo para receber o link de definição ou recuperação de senha.
            </Typography>
            <TextField
              label="E-mail"
              type="email"
              value={resetEmail}
              onChange={(event) => setResetEmail(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setResetOpen(false)}>Cancelar</Button>
          <Button onClick={handleReset} variant="contained" disabled={isSubmitting || !resetEmail} sx={{ borderRadius: 999 }}>
            {isSubmitting ? 'Enviando...' : 'Enviar link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
